import React, { useState } from 'react';
import { Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import type { SplaLicense } from '../../../types/models/Spla';

interface SplaRetireLicenseModalProps {
  license: SplaLicense;
  onClose: () => void;
  /** Performs the archive DELETE; resolves `true` on success (the parent owns the toasts). */
  onRetire: (license: SplaLicense) => Promise<boolean>;
}

/**
 * Confirm retiring one catalog license. Deliberately worded "Retire", never "Delete": the DELETE
 * verb only ARCHIVES — the row stays in the catalog so past reports keep resolving its part number,
 * and it can be restored at any time.
 */
function SplaRetireLicenseModal({ license, onClose, onRetire }: SplaRetireLicenseModalProps) {
  const [isRetiring, setIsRetiring] = useState(false);

  const handleConfirm = async () => {
    setIsRetiring(true);
    const ok = await onRetire(license);
    setIsRetiring(false);
    if (ok) {
      onClose();
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose} aria-label={`Retire license ${license.part_number}`}>
      <ModalHeader title="Retire license" titleIconVariant="warning" />
      <ModalBody>
        <Content component="p">
          Retire <strong>{license.part_number}</strong> ({license.agreement_type}
          {license.agreement_type === 'Academic' ? ` · ${license.offering}` : ''}) —{' '}
          {license.product_family}?
        </Content>
        <Content component="p">
          Retired licenses stay in the catalog — past reports must still resolve their part numbers. The
          row is flagged, stops accepting new consumption, and can be restored at any time.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleConfirm()} isLoading={isRetiring} isDisabled={isRetiring}>
          Retire
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isRetiring}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaRetireLicenseModal;
